import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { saveAuth } from "../auth";
import API_BASE_URL from "../services/config";
import { normalizeAuthUser } from "../utils/normalizeAuthUser";

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  const resetToken = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return query.get("token") || query.get("resetToken") || "";
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("");

    if (!resetToken) {
      setError("This reset link is missing its token.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: resetToken,
          newPassword: password,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.message || data.error || "Could not reset password."
        );
      }

      const userData = normalizeAuthUser(data);

      if (userData) {
        saveAuth(userData);
        setStatus("Password reset successful. Redirecting you home...");
        window.setTimeout(() => {
          navigate("/", { replace: true });
        }, 1200);
        return;
      }

      setStatus(
        "Password reset successful. Please sign in with your new password."
      );
      window.setTimeout(() => {
        navigate("/", { replace: true });
      }, 1500);
    } catch (submitError) {
      console.error("Reset password failed:", submitError);
      setError(submitError.message || "Reset password failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    page: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)",
      color: "#ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
    },
    card: {
      width: "100%",
      maxWidth: "520px",
      background: "rgba(10, 16, 28, 0.92)",
      border: "1px solid rgba(97, 218, 251, 0.25)",
      borderRadius: "24px",
      boxShadow: "0 24px 60px rgba(97, 218, 251, 0.14)",
      padding: "34px 28px",
    },
    icon: {
      width: "72px",
      height: "72px",
      margin: "0 auto 18px",
      borderRadius: "20px",
      background: "linear-gradient(135deg, #61dafb 0%, #21a1f1 100%)",
      color: "#07121d",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "2rem",
      boxShadow: "0 0 24px rgba(97, 218, 251, 0.3)",
    },
    title: {
      textAlign: "center",
      fontSize: "1.8rem",
      fontWeight: 800,
      marginBottom: "10px",
      color: "#61dafb",
    },
    text: {
      textAlign: "center",
      color: "#c7d8ee",
      lineHeight: 1.6,
      marginBottom: "22px",
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    label: {
      color: "#61dafb",
      fontSize: "0.92rem",
      fontWeight: 600,
      marginBottom: "8px",
      display: "block",
    },
    input: {
      width: "100%",
      boxSizing: "border-box",
      background: "rgba(20, 20, 20, 0.9)",
      border: "1.5px solid rgba(97, 218, 251, 0.3)",
      borderRadius: "12px",
      padding: "14px 16px",
      fontSize: "1rem",
      color: "#ffffff",
      outline: "none",
    },
    message: {
      borderRadius: "12px",
      padding: "12px 14px",
      lineHeight: 1.6,
      fontSize: "0.9rem",
    },
    button: {
      background: "linear-gradient(135deg, #61dafb 0%, #21a1f1 100%)",
      color: "#07121d",
      border: "none",
      borderRadius: "12px",
      padding: "14px 18px",
      fontWeight: 800,
      cursor: "pointer",
      marginTop: "4px",
    },
    secondary: {
      background: "rgba(97, 218, 251, 0.08)",
      color: "#b8dbff",
      border: "1px solid rgba(97, 218, 251, 0.2)",
      borderRadius: "12px",
      padding: "12px 18px",
      fontWeight: 700,
      cursor: "pointer",
      marginTop: "12px",
      width: "100%",
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.icon}>{"\uD83D\uDD12"}</div>
        <h1 style={styles.title}>Reset Password</h1>
        <p style={styles.text}>
          Choose a new password for your DharmaAnswers account.
        </p>

        {error && (
          <div
            style={{
              ...styles.message,
              background: "rgba(255, 107, 107, 0.12)",
              border: "1px solid rgba(255, 107, 107, 0.22)",
              color: "#ffb8b8",
              marginBottom: "16px",
            }}
          >
            {error}
          </div>
        )}

        {status && (
          <div
            style={{
              ...styles.message,
              background: "rgba(97, 218, 251, 0.1)",
              border: "1px solid rgba(97, 218, 251, 0.2)",
              color: "#bde9ff",
              marginBottom: "16px",
            }}
          >
            {status}
          </div>
        )}

        <form style={styles.form} onSubmit={handleSubmit}>
          <div>
            <label style={styles.label}>New Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="Enter your new password"
              disabled={isLoading}
            />
          </div>

          <div>
            <label style={styles.label}>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
              placeholder="Confirm your new password"
              disabled={isLoading}
            />
          </div>

          <button type="submit" style={styles.button} disabled={isLoading}>
            {isLoading ? "Resetting Password..." : "Reset Password"}
          </button>
        </form>

        <button
          type="button"
          style={styles.secondary}
          onClick={() => navigate("/", { replace: true })}
        >
          Return Home
        </button>
      </div>
    </div>
  );
};

export default ResetPassword;
